import { type ReactNode, useEffect, useState } from "react";
import { OtsGoBot } from "./OtsGoBot";

export type OtsTableLoaderVariant = "block" | "overlay" | "inline";

export type OtsTableLoaderProps = {
  /** What is loading, e.g. "Loading Tue, Oct 6". Also announced to assistive technology. */
  label: ReactNode;
  /** A quieter second line, e.g. "Tickets, shifts, and geofence visits". */
  description?: ReactNode;
  /**
   * `block` stands in for a table that has nothing to show yet: skeleton rows under the loader card.
   * `overlay` covers a table already on screen (say, yesterday's rows while today's load), dimming it
   * so stale rows are not mistaken for new ones. Its parent needs `position: relative`.
   * `inline` is a small GoBot and label in the flow, for a section of a narrow panel where a card would crowd it.
   */
  variant?: OtsTableLoaderVariant;
  /** Skeleton rows in the `block` variant. */
  rows?: number;
  /**
   * Waits this long before showing, so a quick or cached load doesn't flash. The `block` skeleton shows
   * at once either way; only the card waits.
   */
  delayMs?: number;
  /** Accessible name when `label` is not plain text. */
  ariaLabel?: string;
  className?: string;
};

// Uneven widths read as table content rather than a progress bar.
const ROW_WIDTHS = [100, 92, 100, 84, 100, 96, 88, 100, 78, 100];

const useDelayed = (delayMs: number) => {
  const [shown, setShown] = useState(delayMs <= 0);
  useEffect(() => {
    if (delayMs <= 0) return;
    const timer = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);
  return shown;
};

// An indeterminate sweep, so the loader reads as busy even with motion reduced.
const Sweep = ({ className }: { className: string }) => (
  <span aria-hidden="true" className={["block h-1 overflow-hidden rounded-full bg-[var(--ots-surface-variant)]", className].join(" ")}>
    <span className="block h-full w-1/3 rounded-full bg-[var(--ots-primary)] motion-safe:animate-[ots-table-loader-sweep_1.2s_ease-in-out_infinite]"/>
    <style>{"@keyframes ots-table-loader-sweep{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}"}</style>
  </span>
);

const LoaderCard = ({ label, description }: Pick<OtsTableLoaderProps, "label" | "description">) => (
  <div className="flex items-center gap-3 rounded-xl border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] py-3 pl-3.5 pr-6 shadow-xl">
    <OtsGoBot state="walking" size={52}/>
    <div className="min-w-0">
      <p className="font-[family-name:var(--ots-font-display)] text-base font-semibold text-[var(--ots-secondary)]">{label}</p>
      {description && <p className="text-xs text-[var(--ots-text-muted)]">{description}</p>}
      <Sweep className="mt-2 w-40"/>
    </div>
  </div>
);

/**
 * Foundational loading state for tables and lists, with GoBot walking while the rows arrive. Use `block`
 * for a first load and `overlay` when replacing rows already on screen.
 */
export const OtsTableLoader = ({
  label,
  description,
  variant = "block",
  rows = 8,
  delayMs = 200,
  ariaLabel,
  className,
}: OtsTableLoaderProps) => {
  const shown = useDelayed(delayMs);
  const name = ariaLabel ?? (typeof label === "string" ? label : undefined);

  if (variant === "inline") {
    return (
      <div role="status" aria-live="polite" aria-label={name} data-shown={shown || undefined}
        className={["flex min-h-12 items-center justify-center gap-2 py-3", className].filter(Boolean).join(" ")}>
        {shown && <>
          <OtsGoBot state="walking" size={32}/>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-[var(--ots-secondary)]">{label}</p>
            {description && <p className="text-[11px] text-[var(--ots-text-muted)]">{description}</p>}
            <Sweep className="mt-1 w-24"/>
          </div>
        </>}
      </div>
    );
  }

  if (variant === "overlay") {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={name}
        data-shown={shown || undefined}
        className={[
          "absolute inset-0 z-20 flex justify-center transition-opacity duration-200",
          shown ? "opacity-100" : "pointer-events-none opacity-0",
          className,
        ].filter(Boolean).join(" ")}>
        <div aria-hidden="true" className="absolute inset-0 bg-[var(--ots-background)] opacity-70 backdrop-blur-[1px]"/>
        {/* Near the top rather than centered, so the card is seen on a tall list without scrolling. */}
        <div className="relative mt-16 h-fit">
          {shown && <LoaderCard label={label} description={description}/>}
        </div>
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" aria-label={name} className={["relative", className].filter(Boolean).join(" ")}>
      <div aria-hidden="true" className="space-y-2 p-4">
        {Array.from({ length: rows }, (_, index) => (
          <div
            key={index}
            className="h-9 rounded-md bg-[var(--ots-surface-variant)] motion-safe:animate-pulse"
            style={{ width: `${ROW_WIDTHS[index % ROW_WIDTHS.length]}%`, animationDelay: `${index * 90}ms` }}/>
        ))}
      </div>
      <div className="absolute inset-x-0 top-10 flex justify-center">
        {shown ? <LoaderCard label={label} description={description}/> : <span className="sr-only">{label}</span>}
      </div>
    </div>
  );
};
